import { formatDate, localDayIndex } from '../../utils/dates';

export type CompetitionTab = 'overview' | 'submit' | 'leaderboard';
export type CompetitionAction = 'join' | 'evaluate' | 'manage' | null;

export interface ViewInput {
  status?: string | null;
  hasAccess: boolean | null;
  accessType: string | null;
  isHost: boolean;
  isAdmin: boolean;
}

const TAB_ORDER: CompetitionTab[] = ['overview', 'submit', 'leaderboard'];

/** Same priority as the old Competition.tsx button: judge, then host/admin, then join. */
export function competitionView({ status, hasAccess, accessType, isHost, isAdmin }: ViewInput) {
  const s = (status || '').toLowerCase();
  const isCompetitor = Boolean(hasAccess) && accessType === 'competitor';
  // Hosts and platform admins see the Submit tab too. Admins can submit (the
  // server allows it); a host who isn't a competitor gets a read-only preview.
  // Host/admin are known before access loads, so the tab may show early.
  const canSubmitTab = isCompetitor || isHost || isAdmin;
  const canUpload = isCompetitor || isAdmin;
  const previewOnly = canSubmitTab && !canUpload;
  let action: CompetitionAction = null;
  // Access still loading: show no button until we know who this is.
  if (hasAccess === null) action = null;
  else if (hasAccess && accessType === 'judge') action = 'evaluate';
  else if (isAdmin || isHost) action = 'manage';
  else if ((s === 'open' || s === 'new') && (!hasAccess || accessType === 'viewer')) action = 'join';
  const defaultTab: 'overview' | 'submit' = isCompetitor && s === 'open' ? 'submit' : 'overview';
  return { action, canSubmitTab, canUpload, previewOnly, defaultTab };
}

export function availableTabs(canSubmitTab: boolean, leaderboard?: string | null): CompetitionTab[] {
  return TAB_ORDER.filter((tab) =>
    (tab !== 'submit' || canSubmitTab) && (tab !== 'leaderboard' || leaderboard !== 'unlisted'));
}

export function resolveTab(requested: string | null, tabs: CompetitionTab[], fallback: CompetitionTab): CompetitionTab {
  if (requested && tabs.includes(requested as CompetitionTab)) return requested as CompetitionTab;
  return tabs.includes(fallback) ? fallback : 'overview';
}

export function deadlineText(endDate: string, now: Date = new Date()): string {
  const end = localDayIndex(endDate);
  const today = localDayIndex(now);
  if (end === null || today === null) return '';
  const days = end - today;
  if (days < 0) return `Closed ${formatDate(endDate)}`;
  if (days === 0) return 'Closes today';
  return `Closes ${formatDate(endDate)} · ${days} day${days === 1 ? '' : 's'} left`;
}

/** "12 participants" / "1 team"; null when the count is unknown. */
export function participantsText(count: number | undefined | null, teamMode: boolean): string | null {
  if (count === undefined || count === null) return null;
  const unit = teamMode ? 'team' : 'participant';
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}
